import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Textarea } from "../../ui/textarea";
import { ScrollArea } from "../../ui/scroll-area";
import { Separator } from "../../ui/separator";
import { useToast } from "../../../hooks/use-toast";
import { API_BASE_URL } from "../../../lib/api";
import "./SubProcessEditDialog.css";
const formatCurrency = (value, fractionDigits = 2) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
}).format(value);
const calculateEfficiencyValue = (metrics) => {
    const automationRange = Math.max(metrics.max_automation_percent - metrics.min_automation_percent, 0) / 100;
    return automationRange * metrics.avg_fte_salary * metrics.fte_count;
};
// CRITICAL: Normalize helper function
const normalizeString = (value) => {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    return String(value).toLowerCase().trim();
};
const SubProcessEditDialog = ({ open, onOpenChange, processName, subProcess, uniqueId, onSave, }) => {
    const { toast } = useToast();
    const [localSubProcess, setLocalSubProcess] = useState(subProcess);
    const [guardrails, setGuardrails] = useState([]);
    const [isGuardrailLoading, setIsGuardrailLoading] = useState(false);
    const [isGuardrailSaving, setIsGuardrailSaving] = useState(false);
    useEffect(() => {
        setLocalSubProcess(subProcess);
    }, [subProcess]);
    useEffect(() => {
        const fetchGuardrails = async () => {
            if (!open || !uniqueId) {
                setGuardrails([]);
                return;
            }
            setIsGuardrailLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}/get_editable_fields/${uniqueId}`);
                if (!response.ok) {
                    throw new Error("Unable to load guardrails.");
                }
                const payload = await response.json();
                const allGuardrails = payload.editable_fields?.process_guardrails ?? [];
                // CRITICAL: Normalize target values
                const targetProcessNormalized = normalizeString(processName);
                const normalizedSubProcess = normalizeString(subProcess.sub_process);
                // CRITICAL: Build matching set - include both the normalized sub_process AND the process name
                // because backend stores parent-level items with sub_process = process (not null/empty)
                const candidateSubProcesses = new Set();
                // Always add the actual sub_process value
                candidateSubProcesses.add(normalizedSubProcess);
                // If sub_process matches process name, it's parent-level - add both variations
                if (normalizedSubProcess === targetProcessNormalized) {
                    candidateSubProcesses.add(targetProcessNormalized);
                    candidateSubProcesses.add(""); // Some systems might use empty for parent
                }
                // If sub_process is empty/null, treat as parent-level
                if (!normalizedSubProcess) {
                    candidateSubProcesses.add("");
                    candidateSubProcesses.add(targetProcessNormalized);
                }
                console.log("🔍 Guardrail Fetch Debug:", {
                    processName,
                    targetProcessNormalized,
                    originalSubProcess: subProcess.sub_process,
                    normalizedSubProcess: normalizedSubProcess || "(empty)",
                    isParentLevel: normalizedSubProcess === targetProcessNormalized || !normalizedSubProcess,
                    candidateSubProcesses: Array.from(candidateSubProcesses).map((val) => val || "(empty)"),
                    totalGuardrails: allGuardrails.length
                });
                // CRITICAL: Filter with proper normalization
                const matches = allGuardrails.filter((item) => {
                    // Normalize item values
                    const itemProcessNormalized = normalizeString(item.process);
                    const itemSubProcessNormalized = normalizeString(item.sub_process);
                    // Process must match
                    const processMatch = itemProcessNormalized === targetProcessNormalized;
                    if (!processMatch) {
                        return false;
                    }
                    // Sub-process must match (including empty string for parent-level)
                    const subProcessMatch = candidateSubProcesses.has(itemSubProcessNormalized);
                    if (subProcessMatch) {
                        console.log("✅ Match found:", {
                            process: item.process,
                            sub_process: item.sub_process || "(null/empty)",
                            question: item.question?.substring(0, 50) + "..."
                        });
                    }
                    return subProcessMatch;
                });
                console.log("📊 Matching Results:", {
                    searchingFor: {
                        process: processName,
                        sub_process: normalizedSubProcess || "(empty/parent-level)",
                        candidates: Array.from(candidateSubProcesses).map(c => c || "(empty)")
                    },
                    totalMatches: matches.length,
                    matchedQuestions: matches.map(m => ({
                        question: m.question?.substring(0, 40) + "...",
                        sub_process: m.sub_process || "(null/empty)",
                        hasEfficiency: !!m.directional_guardrails_efficiency,
                        hasEffectiveness: !!m.directional_guardrails_effectiveness
                    }))
                });
                // Debug: If no matches, show what's available
                if (matches.length === 0) {
                    const processMatches = allGuardrails.filter((item) => normalizeString(item.process) === targetProcessNormalized);
                    console.warn("⚠️ No matches found. Available sub-processes for this process:", {
                        process: processName,
                        searchedForSubProcess: normalizedSubProcess || "(empty/parent-level)",
                        candidateSubProcesses: Array.from(candidateSubProcesses).map((val) => val || "(empty)"),
                        availableSubProcesses: [...new Set(processMatches.map((g) => g.sub_process || "(null/empty)"))],
                        sampleQuestions: processMatches.slice(0, 3).map(g => ({
                            sub_process: g.sub_process || "(null/empty)",
                            question: g.question?.substring(0, 50)
                        }))
                    });
                }
                setGuardrails(matches);
            }
            catch (error) {
                const message = error instanceof Error ? error.message : "Failed to load guardrails.";
                console.error("❌ Guardrail fetch error:", error);
                toast({ title: "Guardrails", description: message, variant: "destructive" });
                setGuardrails([]);
            }
            finally {
                setIsGuardrailLoading(false);
            }
        };
        fetchGuardrails();
    }, [open, uniqueId, processName, subProcess.sub_process, toast]);
    const handleEfficiencyChange = (field, value) => {
        setLocalSubProcess((prev) => {
            const updated = {
                ...prev.efficiency_metrics,
                [field]: value,
            };
            return {
                ...prev,
                efficiency_metrics: {
                    ...updated,
                    efficiency_value: calculateEfficiencyValue(updated),
                },
            };
        });
    };
    const handleEffectivenessChange = (field, value) => {
        setLocalSubProcess((prev) => {
            const updated = {
                ...prev.effectiveness_metrics,
                [field]: value,
            };
            return {
                ...prev,
                effectiveness_metrics: {
                    ...updated,
                    total_min: (updated.spend_mgmt_min ?? 0) + (updated.leakage_min ?? 0),
                    total_max: (updated.spend_mgmt_max ?? 0) + (updated.leakage_max ?? 0),
                },
            };
        });
    };
    const handleGuardrailChange = (index, field, value) => {
        setGuardrails((prev) => prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item)));
    };
    const handleSaveGuardrails = async () => {
        if (!uniqueId || guardrails.length === 0) {
            toast({
                title: "No Changes",
                description: "No guardrails available to save.",
                variant: "default"
            });
            return;
        }
        setIsGuardrailSaving(true);
        try {
            const edits = guardrails.flatMap((item) => [
                {
                    section: item.section,
                    process: item.process,
                    sub_process: item.sub_process,
                    question: item.question,
                    field: "directional_guardrails_efficiency",
                    value: item.directional_guardrails_efficiency ?? "",
                },
                {
                    section: item.section,
                    process: item.process,
                    sub_process: item.sub_process,
                    question: item.question,
                    field: "directional_guardrails_effectiveness",
                    value: item.directional_guardrails_effectiveness ?? "",
                },
            ]);
            const response = await fetch(`${API_BASE_URL}/save_edits`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ unique_id: uniqueId, edits }),
            });
            if (!response.ok) {
                const errorPayload = await response.json().catch(() => null);
                throw new Error(errorPayload?.error ?? "Unable to save guardrails.");
            }
            toast({
                title: "Guardrails Saved",
                description: `Successfully updated ${guardrails.length} question(s).`
            });
        }
        catch (error) {
            const message = error instanceof Error ? error.message : "Unable to save guardrails.";
            console.error("❌ Save error:", error);
            toast({ title: "Save Failed", description: message, variant: "destructive" });
        }
        finally {
            setIsGuardrailSaving(false);
        }
    };
    const handlePrimarySave = () => {
        onSave(localSubProcess);
        onOpenChange(false);
    };
    const efficiencySummary = useMemo(() => {
        const metrics = localSubProcess.efficiency_metrics;
        return `${metrics.max_automation_percent} - ${metrics.min_automation_percent}% × ${formatCurrency(metrics.avg_fte_salary, 0)} × ${metrics.fte_count.toFixed(1)} FTEs`;
    }, [localSubProcess.efficiency_metrics]);
    // Compute display name
    const displayName = subProcess.sub_process &&
        normalizeString(subProcess.sub_process) !== normalizeString(processName)
        ? subProcess.sub_process
        : processName;
    return (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="subprocess-dialog">
      <DialogHeader className="subprocess-header">
        <DialogTitle className="subprocess-title">
          Edit Process Inputs — {displayName}
        </DialogTitle>
        <DialogDescription className="subprocess-description">
          Adjust guardrails and input parameters. Values recalculate automatically.
        </DialogDescription>
      </DialogHeader>

        <Tabs defaultValue="efficiency" className="subprocess-tabs">
          <TabsList className="subprocess-tabs-list">
            <TabsTrigger value="efficiency" className="subprocess-tabs-trigger">
              Efficiency
            </TabsTrigger>
            <TabsTrigger value="effectiveness" className="subprocess-tabs-trigger">
              Effectiveness
            </TabsTrigger>
            <TabsTrigger value="guardrails" className="subprocess-tabs-trigger">
              Guardrails
              {guardrails.length > 0 && (<span className="subprocess-tabs-count ml-2">
                  {guardrails.length}
                </span>)}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="efficiency" className="space-y-4 mt-4">
            <div className="subprocess-section-card text-sm">
              <p className="font-medium mb-1">Formula: Automation % × Salary × FTEs</p>
              <p className="text-muted-foreground text-xs">Current: {efficiencySummary}</p>
            </div>

            <div className="subprocess-field-grid">
              <div className="space-y-2">
                <Label>Min Automation %</Label>
                <Input type="number" value={localSubProcess.efficiency_metrics.min_automation_percent} onChange={(e) => handleEfficiencyChange("min_automation_percent", Number(e.target.value))}/>
              </div>
              <div className="space-y-2">
                <Label>Max Automation %</Label>
                <Input type="number" value={localSubProcess.efficiency_metrics.max_automation_percent} onChange={(e) => handleEfficiencyChange("max_automation_percent", Number(e.target.value))}/>
              </div>
              <div className="space-y-2">
                <Label>FTE Count</Label>
                <Input type="number" value={localSubProcess.efficiency_metrics.fte_count} onChange={(e) => handleEfficiencyChange("fte_count", Number(e.target.value))}/>
              </div>
              <div className="space-y-2">
                <Label>Average FTE Salary ($K)</Label>
                <Input type="number" value={localSubProcess.efficiency_metrics.avg_fte_salary / 1000} onChange={(e) => handleEfficiencyChange("avg_fte_salary", Number(e.target.value) * 1000)}/>
              </div>
            </div>

            <div className="subprocess-summary-card">
              <div className="subprocess-summary-label">
                Calculated Efficiency Value
              </div>
              <div className="subprocess-summary-text">
                <span className="font-semibold">
                  {formatCurrency(localSubProcess.efficiency_metrics.efficiency_value)}
                </span>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="effectiveness" className="space-y-4 mt-4">
            <div className="subprocess-section-card text-sm">
              Formula: Spend Management + Value Leakage Prevention
            </div>

            <div className="subprocess-field-grid">
              <div className="space-y-2">
                <Label>Spend Management (Min)</Label>
                <Input type="number" value={localSubProcess.effectiveness_metrics.spend_mgmt_min} onChange={(e) => handleEffectivenessChange("spend_mgmt_min", Number(e.target.value))}/>
              </div>
              <div className="space-y-2">
                <Label>Spend Management (Max)</Label>
                <Input type="number" value={localSubProcess.effectiveness_metrics.spend_mgmt_max} onChange={(e) => handleEffectivenessChange("spend_mgmt_max", Number(e.target.value))}/>
              </div>
              <div className="space-y-2">
                <Label>Leakage Prevention (Min)</Label>
                <Input type="number" value={localSubProcess.effectiveness_metrics.leakage_min} onChange={(e) => handleEffectivenessChange("leakage_min", Number(e.target.value))}/>
              </div>
              <div className="space-y-2">
                <Label>Leakage Prevention (Max)</Label>
                <Input type="number" value={localSubProcess.effectiveness_metrics.leakage_max} onChange={(e) => handleEffectivenessChange("leakage_max", Number(e.target.value))}/>
              </div>
            </div>

            <div className="subprocess-summary-card space-y-1 text-sm">
              <div>
                Total Effectiveness (Min):{" "}
                <span className="font-semibold">
                  {formatCurrency(localSubProcess.effectiveness_metrics.total_min)}
                </span>
              </div>
              <div>
                Total Effectiveness (Max):{" "}
                <span className="font-semibold">
                  {formatCurrency(localSubProcess.effectiveness_metrics.total_max)}
                </span>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="guardrails" className="space-y-4 mt-4">
            {uniqueId ? (<>
                <ScrollArea className="subprocess-guardrails-scroll">
                  {isGuardrailLoading ? (<div className="flex items-center justify-center py-8">
                      <p className="text-sm text-muted-foreground">Loading guardrails…</p>
                    </div>) : guardrails.length === 0 ? (<div className="text-center py-8 space-y-2">
                      <p className="text-sm text-muted-foreground">
                        No guardrails available for this sub-process.
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Check browser console (F12) for detailed matching info.
                      </p>
                    </div>) : (<div className="subprocess-guardrails-list">
                      {guardrails.map((item, index) => (<div key={`${item.question}-${index}`} className="subprocess-guardrail-card">
                          <div>
                            <Label className="subprocess-guardrail-question-label">Question</Label>
                            <p className="subprocess-guardrail-question">{item.question}</p>
                          </div>

                          <div className="space-y-2">
                            <Label className="subprocess-guardrail-field-label">
                              Efficiency Guardrails
                              {(!item.directional_guardrails_efficiency ||
                        item.directional_guardrails_efficiency.trim() === "") && (<span className="subprocess-guardrail-notice">
                                  (Not in source)
                                </span>)}
                            </Label>
                            <Textarea value={item.directional_guardrails_efficiency ?? ""} onChange={(e) => handleGuardrailChange(index, "directional_guardrails_efficiency", e.target.value)} placeholder="Describe directional guardrails for efficiency" className={(!item.directional_guardrails_efficiency ||
                        item.directional_guardrails_efficiency.trim() === "")
                        ? "border-amber-300 bg-amber-50/50"
                        : ""}/>
                          </div>

                          <div className="space-y-2">
                            <Label className="subprocess-guardrail-field-label">
                              Effectiveness Guardrails
                              {(!item.directional_guardrails_effectiveness ||
                        item.directional_guardrails_effectiveness.trim() === "") && (<span className="subprocess-guardrail-notice">
                                  (Not in source)
                                </span>)}
                            </Label>
                            <Textarea value={item.directional_guardrails_effectiveness ?? ""} onChange={(e) => handleGuardrailChange(index, "directional_guardrails_effectiveness", e.target.value)} placeholder="Describe directional guardrails for effectiveness" className={(!item.directional_guardrails_effectiveness ||
                        item.directional_guardrails_effectiveness.trim() === "")
                        ? "border-amber-300 bg-amber-50/50"
                        : ""}/>
                          </div>

                          <Separator />
                        </div>))}
                    </div>)}
                </ScrollArea>

                <Button onClick={handleSaveGuardrails} disabled={isGuardrailSaving || !uniqueId || guardrails.length === 0} className="w-full subprocess-guardrails-save va-button-primary-blue">
                  {isGuardrailSaving ? "Saving Guardrails…" : `Save ${guardrails.length} Guardrail(s)`}
                </Button>
              </>) : (<p className="text-sm text-muted-foreground text-center py-8">
                Unique ID unavailable. Regenerate or upload a new assessment to edit guardrails.
              </p>)}
          </TabsContent>
        </Tabs>

        <DialogFooter className="subprocess-dialog-footer">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handlePrimarySave} className="va-button-primary-blue">Save &amp; Recalculate</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);
};
export default SubProcessEditDialog;
